import { bookingInquiryStrings } from '@data/bookingInquiry';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { partnerStrings } from '@data/partners';
import { BookingInquiryFields } from '@models/next/BookingInquiry';
import { BookingInquiryContact } from '@models/next/Contact';
import { CustomsRequest } from '@models/next/CustomsRequest';
import { CustomsRequestStatusName } from '@models/next/CustomsRequestStatus';
import { DeliveryRequestPickupAddress } from '@models/next/DeliveryRequestPickupAddress';
import {
  DeliveryRequestStatus,
  DeliveryRequestStatusName,
} from '@models/next/DeliveryRequestStatus';
import { DOStatus, DOStatusName } from '@models/next/DOStatus';
import { LocalFile, OptionalLocalFile, RemoteFile } from '@models/next/FileType';
import { MoveOutStatus, MoveOutStatusName } from '@models/next/MoveOutStatus';
import { PaginationType } from '@models/next/PaginationType';
import { Partner } from '@models/next/Partner';
import { PaymentSlip } from '@models/next/PaymentSlip';
import { PreAlertFields } from '@models/next/PreAlert';
import { ServiceName } from '@models/next/RoutePlan';
import { secureDataOf } from '@models/next/SecureData';
import { AdminUser } from '@models/next/User';
import { deepPick } from '@monoid-dev/zod-utils';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const DeliveryRequestURLSubmitForm = z.object({
  partner_id: z.number().label(partnerStrings.truckCompany),
});

export type DeliveryRequestURLSubmitForm = z.infer<typeof DeliveryRequestURLSubmitForm>;

export const DeliveryRequestURLSubmitResult = z.object({
  delivery_request_id: z.number(),
  url: z.string(),
});

export type DeliveryRequestURLSubmitResult = z.infer<typeof DeliveryRequestURLSubmitResult>;

export const DeliveryRequestBatchSubmitAction = z.enum([
  'BATCH_MAIL_SUBMIT',
  'BATCH_CSV_SUBMIT',
  'BATCH_YOKOMOCHI_SUBMIT',
  'BATCH_SENT',
]);

export type DeliveryRequestBatchSubmitAction = z.infer<typeof DeliveryRequestBatchSubmitAction>;

export const DeliveryRequestDeliveryStatusName = z.enum(['お届け中', 'お届け完了', '']);

export type DeliveryRequestDeliveryStatusName = z.infer<typeof DeliveryRequestDeliveryStatusName>;

export const DeliveryRequestDeliveryStatus = z.object({
  name: DeliveryRequestDeliveryStatusName,
  time: z.string(),
});

export type DeliveryRequestDeliveryStatus = z.infer<typeof DeliveryRequestDeliveryStatus>;

export const DeliveryRequestDeliveryInfo = z.object({
  delivery_price: f.yen().label(deliveryRequestStrings.deliveryPrice),
});

export type DeliveryRequestDeliveryInfo = z.infer<typeof DeliveryRequestDeliveryInfo>;

export const DeliveryRequestPickupAddressForm = z.object({
  delivery_destination: DeliveryRequestPickupAddress.shape.delivery_destination.min(1),
  delivery_destination_code: DeliveryRequestPickupAddress.shape.delivery_destination_code,
  repository: DeliveryRequestPickupAddress.shape.repository,
  address: DeliveryRequestPickupAddress.shape.address,
  tel: DeliveryRequestPickupAddress.shape.tel,
  fax: DeliveryRequestPickupAddress.shape.fax,
  principal_name: DeliveryRequestPickupAddress.shape.principal_name,
});

export type DeliveryRequestPickupAddressForm = z.infer<typeof DeliveryRequestPickupAddressForm>;

export const DeliveryRequestPickupAddressFormRequired = z.object({
  delivery_destination: DeliveryRequestPickupAddress.shape.delivery_destination.min(1),
  delivery_destination_code: DeliveryRequestPickupAddress.shape.delivery_destination_code,
  repository: DeliveryRequestPickupAddress.shape.repository.min(1),
  address: DeliveryRequestPickupAddress.shape.address.min(1),
  tel: DeliveryRequestPickupAddress.shape.tel.min(1),
  fax: DeliveryRequestPickupAddress.shape.fax.min(1),
  principal_name: DeliveryRequestPickupAddress.shape.principal_name.min(1).max(32),
});

export type DeliveryRequestPickupAddressFormRequired = z.infer<
  typeof DeliveryRequestPickupAddressFormRequired
>;

export const DeliveryRequestDateType = z.enum(['AM', 'PM', 'TIME']);

export type DeliveryRequestDateType = z.infer<typeof DeliveryRequestDateType>;

export const DriverInfoStatusName = z.enum(['未', '済']);

export type DriverInfoStatusName = z.infer<typeof DriverInfoStatusName>;

/**
 * Entire set of properties on Delivery Request
 */
export const DeliveryRequest = z.object({
  id: z.number(),
  delivery_request_no: z.string().label(deliveryRequestStrings.deliveryRequestNo),
  pickup_date: f.dateString().label(deliveryRequestStrings.pickupDate).nullish(),
  pickup_address: DeliveryRequestPickupAddress,
  delivery_date: z.string().label(deliveryRequestStrings.deliveryDate),
  delivery_date_type: DeliveryRequestDateType.label(deliveryRequestStrings.deliveryDate).nullish(),
  yokomochi_pickup_date: f.dateString().label(deliveryRequestStrings.yokomochiPickupDate).nullish(),
  yokomochi_delivery_date: f
    .dateString()
    .label(deliveryRequestStrings.yokomochiDeliveryDate)
    .nullish(),
  status: DeliveryRequestStatus.array().label(deliveryRequestStrings.status),
  truck_type: z.string().label(deliveryRequestStrings.truckType),
  truck_count: z.number().label(deliveryRequestStrings.truckCount),
  memo: z.string().label(deliveryRequestStrings.memo),
  do_less_no: z.string(),
  do_less_no_file: RemoteFile.nullish(),
  move_out_no: z.string(),
  move_out_no_file: RemoteFile.nullish(),
  delivery_sent_file: RemoteFile.nullish(),
  partner: Partner.nullish(),
  yokomochi_partner: Partner.nullish(),
  delivery_status: DeliveryRequestDeliveryStatus.array(),
  delivery_info: DeliveryRequestDeliveryInfo.nullish(),

  delivery_instruction: z.object({
    receiver: BookingInquiryContact.nullish(),
  }),
  do_status: z.array(DOStatus),
  move_out_status: z.array(MoveOutStatus),
  driver_info_status: DriverInfoStatusName,
  booking_inquiry: deepPick(BookingInquiryFields, {
    d2d_no: true,
    consignee: true,
    receiver: true,
    route_plan: {
      service_name: true,
    },
  }).extend({
    salesperson_japan: AdminUser.nullish(),
  }),

  customs_request: CustomsRequest.pick({
    status: true,
  }).nullish(),

  pre_alert: PreAlertFields.pick({
    quantity: true,
    gross_weight: true,
    measurement: true,
    packages: true,
    atd: true,
    is_manually_locked: true,
    is_ledger_locked: true,
  }),

  payment_slip: PaymentSlip.nullish(),

  creator: AdminUser,

  created_at: z.string(),
  updated_at: z.string(),
});

export type DeliveryRequest = z.infer<typeof DeliveryRequest>;

export const DeliveryRequestSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    delivery_request_no: DeliveryRequest.shape.delivery_request_no,
    truck_type: DeliveryRequest.shape.truck_type,
    truck_count: DeliveryRequest.shape.truck_count,
    d2d_no: BookingInquiryFields.shape.d2d_no,
    consignee: z.string().label('CNEE'),
    pickup_address: z.string().label(bookingInquiryStrings.pickupAddress),
    receiver: z.string().label(bookingInquiryStrings.receiver),
    partner: z.string().label(deliveryRequestStrings.truckCompany),
    pickup_date_after: f.dateString().label(deliveryRequestStrings.pickupDate),
    pickup_date_before: f.dateString().label(deliveryRequestStrings.pickupDate),
    delivery_date_after: f.dateString().label(deliveryRequestStrings.deliveryDate),
    delivery_date_before: f.dateString().label(deliveryRequestStrings.deliveryDate),
    ordering: z.string().array(),
    status: DeliveryRequestStatusName,
    customs_request_status: z.enum([...CustomsRequestStatusName.options, '-']).array(),
    driver_info_status: DriverInfoStatusName,
    delivery_status: DeliveryRequestDeliveryStatusName,
    do_status: DOStatusName,
    move_out_status: MoveOutStatusName.array(),
    service: ServiceName.array(),
    salesperson_japan_id: z.number(),
  })
  .partial();

export type DeliveryRequestSearchQuery = z.infer<typeof DeliveryRequestSearchQuery>;

export const DeliveryRequestListItem = DeliveryRequest;

export type DeliveryRequestListItem = z.infer<typeof DeliveryRequestListItem>;

export const DeliveryRequestPages = z.object({
  count: z.number(),
  results: z.array(DeliveryRequestListItem),
});

export type DeliveryRequestPages = z.infer<typeof DeliveryRequestPages>;

export const DeliveryRequestListItemSecureData = secureDataOf(DeliveryRequestListItem);

export type DeliveryRequestListItemSecureData = z.infer<typeof DeliveryRequestListItemSecureData>;

export const PostDeliveryRequestDeliverySent = DeliveryRequest.pick({
  delivery_sent_file: true,
});

export type PostDeliveryRequestDeliverySent = z.infer<typeof PostDeliveryRequestDeliverySent>;

export const DeliveryRequestBatchSubmitBody = z
  .object({
    partner_id: z.number(),
    time: z.string(),
    delivery_sent_file: RemoteFile,
    yokomochi_partner_id: z.number(),
    yokomochi_pickup_date: DeliveryRequest.shape.yokomochi_pickup_date,
    yokomochi_delivery_date: DeliveryRequest.shape.yokomochi_delivery_date,
  })
  .partial();

export type DeliveryRequestBatchSubmitBody = z.infer<typeof DeliveryRequestBatchSubmitBody>;

export const DeliveryRequestBatchSubmitForm = z.object({
  partner_id: z.number().label(partnerStrings.partner),
});

export type DeliveryRequestBatchSubmitForm = z.infer<typeof DeliveryRequestBatchSubmitForm>;

export const DeliveryRequestBatchYokomochiSubmitForm = z.object({
  yokomochi_partner_id: DeliveryRequestBatchSubmitBody.shape.yokomochi_partner_id
    .unwrap()
    .label(partnerStrings.truckCompany),
  yokomochi_pickup_date: DeliveryRequest.shape.yokomochi_pickup_date,
  yokomochi_delivery_date: DeliveryRequest.shape.yokomochi_delivery_date,
});

export type DeliveryRequestBatchYokomochiSubmitForm = z.infer<
  typeof DeliveryRequestBatchYokomochiSubmitForm
>;

export const DeliveryRequestBatchSentForm = z.object({
  time_date: f.dateString().label(deliveryRequestStrings.sentDate),
  time_time: f.timeString().label(deliveryRequestStrings.sentTime),
  delivery_sent_file: OptionalLocalFile.label(deliveryRequestStrings.deliverySentFile),
});

export type DeliveryRequestBatchSentForm = z.infer<typeof DeliveryRequestBatchSentForm>;

export const DeliveryRequestDeliverySentFileForm = z.object({
  delivery_sent_file: LocalFile.label(deliveryRequestStrings.deliverySentFile),
});

export type DeliveryRequestDeliverySentFileForm = z.infer<
  typeof DeliveryRequestDeliverySentFileForm
>;

const DeliveryRequestFormBase = z.object({
  pickup_date: DeliveryRequest.shape.pickup_date,
  delivery_date_date: f.dateString().label(deliveryRequestStrings.deliveryDate),
  delivery_date_time: z.string().label(deliveryRequestStrings.deliveryDateTime),
  delivery_date_type: DeliveryRequest.shape.delivery_date_type,
  pickup_address: DeliveryRequestPickupAddressForm,
  truck_type: DeliveryRequest.shape.truck_type.min(1),
  truck_count: DeliveryRequest.shape.truck_count,
  memo: DeliveryRequest.shape.memo,
});

export const DeliveryRequestForm = DeliveryRequestFormBase.superRefine((data, ctx) => {
  if (data.delivery_date_type === 'TIME') {
    const e = z
      .object({
        delivery_date_time: DeliveryRequestFormBase.shape.delivery_date_time.min(1),
      })
      .safeParse(data);
    !e.success && e.error.issues.forEach(ctx.addIssue);
  }
  if (data.pickup_address.delivery_destination === 'その他') {
    const e = z
      .object({
        pickup_address: DeliveryRequestPickupAddressFormRequired,
      })
      .safeParse(data);
    !e.success && e.error.issues.forEach(ctx.addIssue);
  }
});

export type DeliveryRequestForm = z.infer<typeof DeliveryRequestForm>;

export const PostDeliveryRequest = DeliveryRequest.pick({
  pickup_date: true,
  delivery_date: true,
  delivery_date_type: true,
  pickup_address: true,
  truck_type: true,
  truck_count: true,
  memo: true,
}).extend({
  d2d_no: BookingInquiryFields.shape.d2d_no,
});

export type PostDeliveryRequest = z.infer<typeof PostDeliveryRequest>;

export const PatchDeliveryRequest = DeliveryRequest.pick({
  pickup_date: true,
  delivery_date: true,
  delivery_date_type: true,
  pickup_address: true,
  truck_type: true,
  truck_count: true,
  memo: true,
  delivery_info: true,
})
  .extend({
    status_name: z.literal('配車完了'),
  })
  .partial();

export type PatchDeliveryRequest = z.infer<typeof PatchDeliveryRequest>;
